import React from 'react';
import {
  NUMBER_OPERATORS,
  conditionCandidates,
  conditionKind,
  conditionOperator,
  newCondition,
} from './formConditions';
import { isMultiSelect } from './selectValue';

// A fork in the path: the mark for "this only appears sometimes", on the
// condition editor and on a collapsed card's badge alike.
export const BranchIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle cx="6" cy="6" r="2.25" />
    <circle cx="6" cy="18" r="2.25" />
    <circle cx="18" cy="8" r="2.25" />
    <path d="M6 8.25v7.5M18 10.25V13a5 5 0 0 1-5 5H8.25" />
  </svg>
);

const CONTROL =
  'bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none';

const questionText = (field) => field.label?.trim() || field.name?.trim() || 'Untitled question';

// "4. Is there damage?" — a question as the picker lists it, numbered as it is
// on its card, and saying why when it can't be chosen.
const candidateLabel = ({ field, number, unavailable }) =>
  `${number}. ${questionText(field)}${unavailable ? ` — ${unavailable}` : ''}`;

// Each run of questions from the same section, gathered under the section's
// name; the form's own top-level questions are left loose.
const groupBySection = (candidates) => candidates.reduce((groups, candidate) => {
  const label = candidate.sections.join(' › ');
  const last = groups[groups.length - 1];
  if (last && last.label === label) last.candidates.push(candidate);
  else groups.push({ label, candidates: [candidate] });
  return groups;
}, []);

const asInputValue = (value) => (typeof value === 'string' || typeof value === 'number' ? value : '');

// How a conditional question reads on its collapsed card — "If Q4 is “Yes”" —
// or, when its condition needs attention, a warning saying so. `check` is the
// entry's result from formConditions.checkConditions.
//
// `editing` is set while the card is open on the condition: one still being
// filled in is only "Conditional" for now, since the editor right below shows
// what's left, and a warning would land the moment the box was ticked.
export const ConditionBadge = ({ check, editing = false }) => {
  if (!check) return null;
  const neutral = 'border border-dashed border-purple-400/50 text-purple-200';
  if (check.problem?.incomplete && editing) {
    return (
      <span className={`shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${neutral}`}>
        <BranchIcon className="w-3 h-3" />
        Conditional
      </span>
    );
  }
  if (check.problem) {
    return (
      <span
        title={check.problem.message}
        className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-400/40"
      >
        <BranchIcon className="w-3 h-3" />
        Check condition
      </span>
    );
  }
  return (
    <span
      title={`Only shown when question ${check.number} (“${questionText(check.field)}”) ${check.answer}`}
      className={`shrink-0 min-w-0 max-w-[14rem] inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${neutral}`}
    >
      <BranchIcon className="w-3 h-3 shrink-0" />
      <span className="truncate">If Q{check.number} {check.answer}</span>
    </span>
  );
};

// The condition on a question or a section, set out as the sentence it stands
// for: "Only show this question when [question] [is] [answer]". The question
// comes first, and what the answer box is follows from that question's type —
// free text for text and contact, one of the options for a dropdown, photo or
// no photo, a comparison with a number for a number or calculation, or a date.
//
// `problem` is the entry's check from formConditions.checkConditions. A
// condition that has broken says why underneath; an unfinished one doesn't
// need to — the empty box it is waiting on already says what's missing.
const ConditionEditor = ({ entry, entries, subject, problem, onChange }) => {
  const condition = entry.condition || newCondition(null);
  const candidates = conditionCandidates(entries, entry.id);
  const field = candidates.find((candidate) => candidate.field.id === condition.fieldId)?.field || null;
  const kind = field ? conditionKind(field) : null;
  const listId = `condition-presets-${entry.id}`;

  const update = (changes) => onChange({ ...condition, ...changes });

  // A new question starts its answer afresh: an answer chosen for one question
  // means nothing to the next.
  const chooseQuestion = (fieldId) =>
    onChange(newCondition(candidates.find((candidate) => candidate.field.id === fieldId)?.field || null));

  const connective = (word) => <span className="shrink-0 text-sm text-purple-100">{word}</span>;

  const answerControls = () => {
    switch (kind) {
      case 'photo':
        return (
          <select
            value={typeof condition.value === 'boolean' ? String(condition.value) : ''}
            onChange={(e) => update({ value: e.target.value === 'true' })}
            aria-label="Whether it waits for a photo"
            className={`${CONTROL} w-0 flex-1 min-w-[9rem]`}
          >
            {typeof condition.value !== 'boolean' && <option value="" disabled>Choose…</option>}
            <option value="true">has a photo</option>
            <option value="false">has no photo</option>
          </select>
        );
      case 'number':
        return (
          <>
            <select
              value={conditionOperator(condition)}
              onChange={(e) => update({ operator: e.target.value })}
              aria-label="How to compare"
              className={`${CONTROL} shrink-0`}
            >
              {NUMBER_OPERATORS.map((operator) => (
                <option key={operator.value} value={operator.value}>{operator.label}</option>
              ))}
            </select>
            <input
              type="number"
              step="any"
              value={asInputValue(condition.value)}
              onChange={(e) => update({ value: e.target.value })}
              placeholder="Number"
              aria-label="Number to compare with"
              className={`${CONTROL} w-28 min-w-[4rem]`}
            />
          </>
        );
      case 'choice': {
        // Options still being typed in are blank, and a list can repeat itself;
        // neither is anything to choose between.
        const options = [...new Set((Array.isArray(field.options) ? field.options : []).filter(Boolean))];
        return (
          <>
            {connective(isMultiSelect(field) ? 'includes' : 'is')}
            <select
              value={options.includes(condition.value) ? condition.value : ''}
              onChange={(e) => update({ value: e.target.value })}
              aria-label="Answer it waits for"
              className={`${CONTROL} w-0 flex-1 min-w-[8rem]`}
            >
              <option value="" disabled>
                {options.length > 0 ? 'Choose an answer…' : 'This dropdown has no options yet'}
              </option>
              {options.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </>
        );
      }
      case 'date':
        return (
          <>
            {connective('is')}
            <input
              type="date"
              value={asInputValue(condition.value)}
              onChange={(e) => update({ value: e.target.value })}
              aria-label="Date it waits for"
              className={CONTROL}
            />
          </>
        );
      default: {
        // A text question's presets are the answers it's most likely to get,
        // so they're offered as suggestions — the box still takes anything.
        const presets = kind === 'text' && Array.isArray(field.presets)
          ? [...new Set(field.presets.filter((preset) => preset?.trim()))]
          : [];
        return (
          <>
            {connective('is')}
            <input
              type="text"
              value={asInputValue(condition.value)}
              onChange={(e) => update({ value: e.target.value })}
              placeholder={kind === 'contact' ? 'Name, phone, email or address' : 'Answer'}
              aria-label="Answer it waits for"
              list={presets.length > 0 ? listId : undefined}
              className={`${CONTROL} w-0 flex-1 min-w-[8rem]`}
            />
            {presets.length > 0 && (
              <datalist id={listId}>
                {presets.map((preset) => <option key={preset} value={preset} />)}
              </datalist>
            )}
          </>
        );
      }
    }
  };

  let hint = null;
  if (kind === 'text') hint = 'Capitals and extra spaces don’t matter.';
  if (kind === 'contact') {
    hint = 'Matches the contact’s name, phone, email or address. Capitals and extra spaces don’t matter.';
  }

  return (
    <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-100">
          <BranchIcon />
          Only show this {subject} when
        </span>
        <select
          value={field ? field.id : ''}
          onChange={(e) => chooseQuestion(e.target.value)}
          disabled={candidates.length === 0}
          aria-label={`Question this ${subject} depends on`}
          className={`${CONTROL} flex-[2_1_14rem] min-w-0 disabled:opacity-60`}
        >
          <option value="" disabled>
            {candidates.length > 0 ? 'Choose a question…' : 'No other questions on this form yet'}
          </option>
          {groupBySection(candidates).map((group, index) => {
            const options = group.candidates.map((candidate) => (
              <option
                key={candidate.field.id}
                value={candidate.field.id}
                disabled={!!candidate.unavailable}
              >
                {candidateLabel(candidate)}
              </option>
            ));
            return group.label ? (
              // Two runs of the same section, split by a subsection between
              // them, share a name — hence the position in the key.
              <optgroup key={`${index}-${group.label}`} label={group.label}>{options}</optgroup>
            ) : options;
          })}
        </select>
        {/* The answer's pieces wrap as one, so a comparison never ends a
            line with the number it compares against starting the next. Sized
            from what it holds, so that's the width it asks for; a phone's
            line can still squeeze it, the number box giving way first. */}
        {field && <div className="flex flex-auto min-w-0 items-center gap-2">{answerControls()}</div>}
      </div>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {problem && !problem.incomplete && (
        <p className="text-xs text-amber-300">{problem.message}</p>
      )}
    </div>
  );
};

export default ConditionEditor;
